/**
 * Hands control to the newest service worker before the caller navigates.
 *
 * A reload alone is not enough when a worker is in control: the navigation goes
 * through its fetch handler and is answered from the SAME precached `index.html` the
 * user is trying to get off, so the page comes back just as stale. Fetching the
 * current SW script (it may carry a new precache manifest), then telling a
 * waiting/installing worker to `skipWaiting()`, is what makes the next navigation
 * land on the fresh shell.
 *
 * Every step is best-effort and time-bounded — the caller navigates either way, so a
 * stalled worker must never be able to strand it.
 */

const CONTROLLERCHANGE_TIMEOUT_MS = 3_000

export async function activateNewestServiceWorker(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration()
    if (!registration) return

    // Pick up a just-deployed worker in case this tab hasn't noticed it yet. Bounded
    // like the wait below, so a stalled SW-script fetch can't hold up navigation.
    await Promise.race([
      registration.update().catch(() => {
        /* best-effort: a failed SW-script fetch shouldn't block navigation */
      }),
      new Promise<void>((resolve) =>
        window.setTimeout(resolve, CONTROLLERCHANGE_TIMEOUT_MS),
      ),
    ])

    const pending = registration.waiting ?? registration.installing
    if (!pending) return

    await new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => resolve(),
        { once: true },
      )
      // vite-plugin-pwa's generated SW calls `skipWaiting()` on this message.
      pending.postMessage({ type: 'SKIP_WAITING' })
      // Don't hang if the SW never takes control — the caller proceeds regardless.
      window.setTimeout(resolve, CONTROLLERCHANGE_TIMEOUT_MS)
    })
  } catch {
    /* best-effort — the caller navigates regardless */
  }
}
